/**
 * What the auth callback does once `authenticateByUrl` has produced a live
 * session, kept out of the route component so the branch can be tested without
 * a Stytch client or a DOM.
 *
 * Every accepted invitation now lands *in the console*. It used to be the
 * other way round: a generic invitation was intercepted by a desktop handoff
 * card and only a feedback-scoped one redirected, which made the browser the
 * exception. The browser is the front door, so the redirect is the rule and
 * there is no interception left to be the exception to it.
 *
 * Resolving the org did not become pointless when the card went away — it is
 * what lets the console land the invitee *in the team they just joined* rather
 * than on an org picker, and what lets the welcome state name it.
 */

import { resolveConsoleRoute, sanitizeReturnPath } from '../routing';

/** The Stytch token type an accepted team invitation arrives with. */
const INVITATION_TOKEN_TYPE = 'multi_tenant_magic_links';

export function isInvitationCallback(search: string): boolean {
  return new URLSearchParams(search).get('stytch_token_type') === INVITATION_TOKEN_TYPE;
}

/** An organization as any of the resolution sources can describe it. */
export interface OrgIdentity {
  orgId: string;
  orgName: string | null;
}

/** The organization an invitation was accepted into, as far as we resolved it. */
export interface InvitationHandoff {
  /** Null only when no source could name the org; the card still renders. */
  orgId: string | null;
  orgName: string | null;
  email?: string;
  projectNames?: string[];
}

/**
 * Where the callback sends the caller. There is only one kind now: everything
 * redirects into the console. `invitation` rides along when the session was
 * established by accepting one, so the landing can address the right org and
 * the welcome state can name it.
 */
export type SessionOutcome = {
  kind: 'redirect';
  path?: string;
  invitation?: InvitationHandoff;
};

interface MagicLinkAuthenticateData {
  organization?: { organization_id?: string; organization_name?: string };
  member?: { email_address?: string };
  /** Present when the link established a full member session. */
  member_session?: unknown;
  session_jwt?: unknown;
  /** Present instead when the link only got as far as discovery. */
  intermediate_session_token?: unknown;
}

/** What the authenticate response says about the session the link established. */
export type AuthenticateSessionState = 'member' | 'intermediate' | 'unknown';

/**
 * Read the session state off the authenticate response.
 *
 * The callback used to answer this by reading the SDK's cached token the
 * instant `authenticate` resolved, and treated an empty read as proof that no
 * member session existed. It is not proof of anything — `getFreshBrowserSessionJwt`
 * has always treated an empty `getTokens()` as ordinary and refreshed past it.
 * When the guess was wrong the callback ran discovery against a full member
 * session, which fails, and told an invitee who had just joined that their link
 * was dead (NIM-5382). The response itself answers the question, so ask it.
 *
 * `unknown` is a real third answer, not a default: a token type the SDK did not
 * handle produces no authenticate body at all, and the caller falls back to the
 * token cache there rather than inventing a branch.
 */
export function readAuthenticateSessionState(result: unknown): AuthenticateSessionState {
  const data = readAuthenticateResult(result);
  if (data.member_session || typeof data.session_jwt === 'string') return 'member';
  if (typeof data.intermediate_session_token === 'string') return 'intermediate';
  return 'unknown';
}

/**
 * `authenticateByUrl` resolves to `{ handled, tokenType, data }`, where `data`
 * is the magic-link authenticate response. The flat response is accepted too so
 * a caller holding the inner object directly still resolves.
 */
export function readAuthenticateResult(result: unknown): MagicLinkAuthenticateData {
  if (!result || typeof result !== 'object') return {};
  const envelope = result as { data?: unknown };
  const inner = envelope.data && typeof envelope.data === 'object' ? envelope.data : result;
  return inner as MagicLinkAuthenticateData;
}

/**
 * Every place the org can be read from, in the order the callback should try
 * them. Functions rather than values so nothing is fetched once an earlier,
 * cheaper source has already answered — discovery is a round trip.
 */
export interface OrgResolutionSources {
  /** The org-scoped session's cached organization, when Stytch already holds one. */
  sessionOrganization: () => OrgIdentity | null;
  /** The member session's org id, present even when the organization object is not. */
  sessionOrganizationId: () => string | null;
  /** Discovery listing — the last resort, and the only source costing a round trip. */
  listDiscoveredOrganizations: () => Promise<OrgIdentity[]>;
}

function identity(orgId: string | undefined, orgName?: string | null): OrgIdentity | null {
  return orgId ? { orgId, orgName: orgName || null } : null;
}

async function discoveredIdentity(
  sources: OrgResolutionSources,
  knownOrgId: string | null,
): Promise<OrgIdentity | null> {
  let discovered: OrgIdentity[];
  try {
    discovered = await sources.listDiscoveredOrganizations();
  } catch (cause) {
    // The invitee is already a member; failing to list is not failing to join.
    console.warn('[auth] could not list organizations for the invitation handoff', cause);
    return null;
  }
  if (knownOrgId) return discovered.find(org => org.orgId === knownOrgId) || null;
  return discovered.length === 1 ? discovered[0]! : null;
}

/**
 * Resolve the invited org from the authenticate response, then from the session
 * it just established, then from discovery. An already-signed-in invitee gets a
 * response with no `organization` on it — that case is the whole reason the
 * later sources exist.
 */
async function resolveInvitationHandoff(
  invite: MagicLinkAuthenticateData,
  sources: OrgResolutionSources,
): Promise<InvitationHandoff> {
  const email = invite.member?.email_address;
  const fromResult = identity(invite.organization?.organization_id, invite.organization?.organization_name);
  const resolved = fromResult
    || sources.sessionOrganization()
    || await discoveredIdentity(sources, sources.sessionOrganizationId());

  return { orgId: resolved?.orgId ?? null, orgName: resolved?.orgName ?? null, email };
}

/**
 * The console path an accepted invitation lands on.
 *
 * `orgId` is carried explicitly rather than left to the root redirect's
 * "active org, else the first team" guess: an invitee who already belonged to
 * another org would otherwise be dropped into whichever one sorted first,
 * having just been told they joined a different one.
 */
export function invitationLandingPath(orgId: string | null): string {
  const query = new URLSearchParams({ invitation: 'accepted' });
  if (orgId) query.set('orgId', orgId);
  return `/?${query.toString()}`;
}

export async function resolveSessionOutcome(input: {
  isInvitation: boolean;
  /** Same-tab cold-link state, preferred over the email relay when present. */
  rememberedReturnPath?: string | null;
  /** Fresh-tab invite state forwarded by the worker relay. */
  relayedReturnPath?: string | null;
  result: unknown;
  sources: OrgResolutionSources;
}): Promise<SessionOutcome> {
  if (!input.isInvitation) return { kind: 'redirect' };
  const returnPath = sanitizeReturnPath(input.rememberedReturnPath ?? input.relayedReturnPath ?? null);
  const returnRoute = returnPath
    ? resolveConsoleRoute(new URL(returnPath, 'https://console.nimbalyst.com').pathname)
    : null;
  // Preserve the addressed work, including its question/comment anchors.
  // Organization/project access is checked by the destination route.
  if (returnPath && (returnRoute?.kind === 'feedbackRequest' || returnRoute?.kind === 'document')) {
    return { kind: 'redirect', path: returnPath };
  }
  const invitation = await resolveInvitationHandoff(readAuthenticateResult(input.result), input.sources);
  return { kind: 'redirect', path: invitationLandingPath(invitation.orgId), invitation };
}

// `joinedTeamDetails` lived here to name the org and its projects for the
// handoff card, off a `listTeams` call the callback made for that one purpose.
// The invitee now lands inside the console, where the route context already
// holds the resolved team and project, so the extra round trip and the second
// naming path both went with the card.
